import { message } from 'antd';
import { useSignedUser } from 'api/auth';
import { useStoreDetails } from 'api/store';
import { useCallback, useEffect, useMemo } from 'react';
import { onboardingStepManualToggle } from 'api/onboarding_sdk';

export enum OnboardingStep {
  createAccount,
  createStore,
  firstProduct,
  shareStore,
}
export interface StepItem {
  id: OnboardingStep;
  title: string;
  description: string;
  action?: (id: OnboardingStep) => void;
}

interface OnboardingValues {
  completedSteps: OnboardingStep[];
  steps: StepItem[];
  activeStep: StepItem;
  isCompleted: boolean;
  initialCollapseStatus: boolean;
  handleCollapseStatus: (status: boolean) => void;
  toggleStepComplete: (step: OnboardingStep, completed: boolean) => void;
}

const steps: StepItem[] = [
  {
    id: OnboardingStep.createAccount,
    title: 'Create your account',
    description: '2 minutes',
  },
  {
    id: OnboardingStep.createStore,
    title: 'Create your first store',
    description: '3 minutes',
  },
  {
    id: OnboardingStep.firstProduct,
    title: 'Add or edit your products',
    description: '4 minutes',
  },
  {
    id: OnboardingStep.shareStore,
    title: 'Share your store with guests',
    description: '2 minutes',
  },
];

interface Props {
  storeId: number;
  actions: Partial<Record<OnboardingStep, StepItem['action']>>;
}

const getCompletedSteps = (store, ownerId): OnboardingStep[] => {
  const items: OnboardingStep[] = [];
  if (ownerId) {
    items.push(OnboardingStep.createAccount);
  }

  if (store?.id) {
    items.push(OnboardingStep.createStore);
  }

  if (store?.hasAddedProduct) {
    items.push(OnboardingStep.firstProduct);
  }

  if (store?.hasSharedStore) {
    items.push(OnboardingStep.shareStore);
  }
  return items;
};

const saveCacheCollapseStatus = (storeId, status: boolean) => {
  localStorage.setItem('onboardingStatus', JSON.stringify({ [storeId]: status }));
};

const getCacheCollapseStatus = (storeId: number) => {
  const cacheStatus = JSON.parse(localStorage.getItem('onboardingStatus'));
  return cacheStatus?.[storeId] ?? true;
};

export default function useOnboardingSteps({ storeId, actions }: Props): OnboardingValues {
  const { data: store, mutate: mutateStoreDetails } = useStoreDetails(storeId, true);
  const { data: user } = useSignedUser();

  const initialCompletedSteps = useMemo(
    () => getCompletedSteps(store, user?.owner_id),
    [user, store],
  );

  const completedSteps = useMemo(() => getCompletedSteps(store, user?.owner_id), [user, store]);

  const initialCollapseStatus = useMemo(() => getCacheCollapseStatus(store?.id), [store]);

  const proxyAction = useCallback(
    (step: StepItem) => (id: OnboardingStep) => {
      const action = actions?.[step.id] ?? (() => null);
      if (id === step.id) {
        return action(id);
      }
      return (() => null)();
    },
    [actions],
  );

  const handleCollapseStatus = (status) => {
    if (!store?.id) {
      return;
    }
    saveCacheCollapseStatus(store.id, status);
  };

  const toggleStepComplete = useCallback(
    (step: OnboardingStep, completed: boolean) => {
      const stepMap: Partial<Record<OnboardingStep, 'product' | 'share'>> = {
        [OnboardingStep.firstProduct]: 'product',
        [OnboardingStep.shareStore]: 'share',
      };

      const apiStep = stepMap[step];

      if (!apiStep) {
        return;
      }

      onboardingStepManualToggle({
        storeId,
        step: apiStep,
        completed,
      })
        .then(() => {
          mutateStoreDetails();
        })
        .catch(() => {
          message.error('Could not update onboarding step');
        });
    },
    [storeId, mutateStoreDetails],
  );

  const activeStep = useMemo(() => {
    const tempSteps = structuredClone(steps).filter(({ id }) => !completedSteps.includes(id));
    const step = tempSteps.shift();

    if (!step) {
      return steps[steps.length - 1];
    }

    return {
      ...step,
      action: proxyAction(step),
    };
  }, [completedSteps.length, proxyAction]);

  const isCompleted = useMemo(
    () => completedSteps.length === steps.length,
    [completedSteps.length],
  );

  useEffect(() => {
    if (isCompleted && initialCompletedSteps.length !== completedSteps.length) {
      message.success('You’ve successfully completed your store set-up!');
    }
  }, [isCompleted]);

  return {
    isCompleted,
    completedSteps,
    steps,
    activeStep,
    initialCollapseStatus,
    handleCollapseStatus,
    toggleStepComplete,
  };
}
